import type { ApolloError } from '@apollo/client';
import type { RenderParams } from '@seek/forms-ui';
import { useTranslations } from '@vocab/react';
import { Card, Heading, Stack, useToast } from 'braid-design-system';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import appTranslations from 'src/App/App.vocab';
import {
  SkeletonEditUserDetails,
  SkeletonEditUserPermissions,
} from 'src/App/Components/Skeleton/SkeletonEditUser/SkeletonEditUser';
import { createSuccessToast } from 'src/App/Components/ToastMessage/ToastMessages';
import translations from 'src/App/Components/UserFormProvider/.vocab';
import {
  CLICK_ADD_USER_STEP2_CANCEL,
  CLICK_EDIT_USER_CANCEL,
  PAGE_EDIT_USER_DISPLAYED,
} from 'src/constants/tracking';
import { BASE_URL } from 'src/constants/urls';
import { MAX_ADMIN_COUNT } from 'src/constants/user';
import {
  type AddUserToAccountInput,
  Role,
  type UpdateUserPermissionsInput,
  type UserDetails,
  type HirerUserEmailFoundPayloadV2,
  HirerUserStatus,
} from 'src/graphql/generated';
import { useAccountContext } from 'src/hooks/use-account-context';
import { useAddUserToAccount } from 'src/hooks/use-add-user-to-account';
import { useReinstateUserStatus } from 'src/hooks/use-reinstate-user-status';
import { useTalentSearchContext } from 'src/hooks/use-talent-search-context';
import { useUpdateEditedPermissions } from 'src/hooks/use-update-edited-permissions';
import { useUserList } from 'src/hooks/use-user-list';
import { useConfig } from 'src/modules/ConfigProvider';

import {
  trackAddUserSuccess,
  trackAlertShown,
  trackClickAddUserStep2Submit,
  trackClickUpdateUser,
  trackUpdateUserSuccess,
} from '../../../../utils/trackingUserActions';
import { type UserFormValues, useFields } from '../userFormFields';

import { FormActions, FormPermissions, FormRoles, FormUserDetails } from '.';

interface FormRootProps extends RenderParams<UserFormValues> {
  isAddFlow: boolean;
  isEditFlow: boolean;
  email: string;
  userExistingOnAdd?: HirerUserEmailFoundPayloadV2;
  userToEdit?: UserDetails;
}

export const FormRoot = ({
  isAddFlow,
  isEditFlow,
  email,
  handleSubmit,
  setFieldValue,
  userExistingOnAdd,
  userToEdit,
}: FormRootProps) => {
  const { data: accountContext, loading: accountLoading } = useAccountContext();
  const { data: accountUsers, loading: usersLoading } = useUserList();
  const { accountHasPremiumTalentSearch, loading: talentSearchLoading } =
    useTalentSearchContext();
  const showToast = useToast();
  const navigate = useNavigate();
  const { urlResolver } = useConfig();
  const { t } = useTranslations(translations);
  const { t: appTrans } = useTranslations(appTranslations);

  const [genericFormError, setGenericFormError] = useState(false);

  const { mutate: addUserToAccount, loading: loadingAddUserToAccount } =
    useAddUserToAccount();
  const {
    mutate: updateUserPermissions,
    loading: loadingUpdateUserPermissions,
  } = useUpdateEditedPermissions();
  const { mutate: reinstateUserStatus } = useReinstateUserStatus();

  const adminsOnAccount = accountUsers?.adminsList?.users;
  const hasMaxNumberOfAdminsOnAccount = adminsOnAccount
    ? adminsOnAccount.length >= MAX_ADMIN_COUNT
    : false;

  const {
    firstName,
    lastName,
    role,
    hasAllPositionAccess,
    hasAnalytics,
    hasPremiumTalentSearch,
    canCreatePosition,
  } = useFields(isAddFlow, Boolean(userExistingOnAdd));

  const isAdmin = role.value === Role.ADMINISTRATOR;
  const flowProps = {
    disableAllRoles: isAddFlow
      ? false
      : accountContext?.headerFooterContext?.user?.id === userToEdit?.id,
    disableAdminRole: isAddFlow
      ? hasMaxNumberOfAdminsOnAccount
      : hasMaxNumberOfAdminsOnAccount &&
        userToEdit?.permissions?.role.toUpperCase() !== Role.ADMINISTRATOR,
    cancelClickAnalytics: isAddFlow
      ? CLICK_ADD_USER_STEP2_CANCEL
      : CLICK_EDIT_USER_CANCEL,
    cancelClickAnalyticsOrigin: isAddFlow
      ? undefined
      : PAGE_EDIT_USER_DISPLAYED,
    successToastMessage: isAddFlow
      ? t('Team member added')
      : t('Changes saved'),
    genericErrorMessage: isAddFlow
      ? t('Add team member error message')
      : t('Edit team member permissions error message'),
    submitButtonText: isAddFlow
      ? t('Add team member button')
      : t('Save changes button'),
    trackSubmissionClick: isAddFlow
      ? trackClickAddUserStep2Submit
      : trackClickUpdateUser,
    trackActionSuccess: isAddFlow
      ? trackAddUserSuccess
      : trackUpdateUserSuccess,
  };

  const onRoleTypeChange = (changeEvent: React.ChangeEvent<any>) => {
    const selectedRole = changeEvent?.target?.value;
    const adminIsSelected = selectedRole === Role.ADMINISTRATOR;

    if (isAddFlow) {
      setFieldValue('hasAnalytics', adminIsSelected);
      setFieldValue('hasAllPositionAccess', true);
      setFieldValue('hasPremiumTalentSearch', adminIsSelected);
      setFieldValue('canCreatePosition', true);
    } else {
      if (adminIsSelected) {
        setFieldValue('hasAllPositionAccess', true);
        setFieldValue('hasPremiumTalentSearch', true);
        setFieldValue('canCreatePosition', true);
      }

      if (selectedRole === Role.USER && !accountHasPremiumTalentSearch) {
        setFieldValue('hasPremiumTalentSearch', false);
      }
    }

    role.onChange(changeEvent);
  };

  const getUserNames = (formValues: UserFormValues) => {
    const userNames = {
      firstName: formValues.firstName,
      lastName: formValues.lastName,
    };

    if (userExistingOnAdd || isEditFlow) {
      userNames.firstName = userNames.lastName = '';
    }

    return userNames;
  };

  // @ts-ignore:next-line
  const onSubmit = handleSubmit(async (formValues) => {
    flowProps.trackSubmissionClick();
    try {
      if (isAddFlow) {
        if (!userExistingOnAdd) {
          const addUserToAccountInput: AddUserToAccountInput = {
            email,
            firstName: formValues.firstName.trim(),
            lastName: formValues.lastName.trim(),
            originZone: formValues.originZone,
          };

          await addUserToAccount({
            variables: { input: addUserToAccountInput },
          });
        }

        if (userExistingOnAdd?.status === HirerUserStatus.DEACTIVATED) {
          await reinstateUserStatus({
            variables: {
              input: {
                userId: Number(userExistingOnAdd?.hirerUserId),
              },
            },
          });
        }
      }
      const updateUserPermissionsInput: UpdateUserPermissionsInput = {
        userInfo: {
          email,
          ...getUserNames(formValues),
          permissions: {
            hasPremiumTalentSearch: formValues.hasPremiumTalentSearch,
            hasAllPositionAccess: formValues.hasAllPositionAccess,
            hasAnalytics: formValues.hasAnalytics,
            canCreatePosition: formValues.canCreatePosition,
            role: formValues.role,
          },
        },
      };

      await updateUserPermissions({
        variables: { updateUserPermissionsInput },
      });

      const successToast = createSuccessToast(flowProps.successToastMessage);
      showToast(successToast);

      const permissionsArray = [
        formValues.canCreatePosition && 'job posting',
        formValues.hasAnalytics && 'manager reports',
        formValues.hasAllPositionAccess && 'job access',
      ];
      flowProps.trackActionSuccess(
        formValues.role,
        permissionsArray.join(' | '),
      );

      navigate(urlResolver({ path: `/${BASE_URL}` }));
    } catch (error) {
      const err = error as ApolloError;

      const isProcessingError = err.graphQLErrors.find(
        (gqlError) => (gqlError.extensions?.response as any).status === 422,
      );
      if (isProcessingError) {
        setGenericFormError(true);
        trackAlertShown.apply(
          this,
          isAddFlow
            ? [flowProps.genericErrorMessage]
            : [flowProps.genericErrorMessage, userToEdit?.id],
        );
      }
    }
  });

  return (
    <form onSubmit={onSubmit}>
      <Stack space="large">
        {isAddFlow && (
          <Stack space="large">
            <Heading level="3">{appTrans('Individual details')}</Heading>
            <Card>
              {usersLoading ? (
                <SkeletonEditUserDetails />
              ) : (
                <FormUserDetails
                  email={email}
                  firstName={firstName}
                  lastName={lastName}
                  user={userExistingOnAdd}
                />
              )}
            </Card>
          </Stack>
        )}
        <Stack space="large">
          <Heading level="3">{t('Permissions')}</Heading>
          <Card>
            {talentSearchLoading || accountLoading ? (
              <SkeletonEditUserPermissions />
            ) : (
              <Stack space="large" dividers>
                <FormRoles
                  disableAllRoles={flowProps.disableAllRoles}
                  disableAdminRole={flowProps.disableAdminRole}
                  role={role}
                  onRoleTypeChange={onRoleTypeChange}
                />
                <FormPermissions
                  accountHasPremiumTalentSearch={accountHasPremiumTalentSearch}
                  canCreatePosition={canCreatePosition}
                  hasAllPositionAccess={hasAllPositionAccess}
                  hasAnalytics={hasAnalytics}
                  hasPremiumTalentSearch={hasPremiumTalentSearch}
                  disableSelectPermissions={isAdmin}
                />
              </Stack>
            )}
          </Card>
        </Stack>
        <FormActions
          cancelButtonData={{
            analytics: flowProps.cancelClickAnalytics,
            analyticsOrigin: flowProps.cancelClickAnalyticsOrigin,
          }}
          displayGenericFormError={genericFormError}
          genericErrorMessage={flowProps.genericErrorMessage}
          submitButtonLoading={
            loadingAddUserToAccount || loadingUpdateUserPermissions
          }
          submitButtonText={flowProps.submitButtonText}
        />
      </Stack>
    </form>
  );
};
